/* ==========================================================================
   wc2-price.js — 料金のめやす / お見積もりの見方 ページの挙動
   --------------------------------------------------------------------------
   依存なし。defer で読み込む。

   受け持つもの
     - 実例カード → 内訳のシート(スマホ)/ モーダル(PC)の開閉
       中身は Liquid が組み立てた隠しブロックをそのまま差し込む(JSでHTMLを組まない)
     - Esc・背景タップで閉じる / フォーカスをシートに閉じ込める / 閉じたら元の要素へ戻す
     - 実例レールの横スクロール矢印(PCのみ表示。端で無効化)
     - レシートの「?」→ シートを閉じてその項目へ移動しハイライト
     - 解剖図のピン・部位リスト → 該当項目へ移動
     - ハッシュ(#opt-... など)で開いたときの移動。折りたたみの中なら開く
     - 固定ヘッダーの実高さを測って --wc2-header-h に入れる
       (移動先の scroll-margin-top がこの値を使う)
     - 物差しのフェードイン(IntersectionObserver)
     - prefers-reduced-motion では動きを止める

   セクションが複数あっても1回だけ初期化する(ガード付き)。
   テーマエディタでのセクション再描画にも対応する。
   ========================================================================== */
(function () {
  'use strict';

  var FLASH_MS = 2600;
  var ANCHOR_GAP = 24; /* 移動先の上端と、ヘッダーの下端とのすき間(px) */

  var reduce = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function prefersReduce() {
    return !!reduce.matches;
  }

  /* ------------------------------------------ 固定ヘッダーの高さを測る

     Dawn は :root に --header-height を出しているが、テーマの設定や
     スクロールで縮むヘッダーでは実際の高さとずれることがある。
     いま画面の上に貼りついている要素の実高さを測って
     --wc2-header-h に入れ、CSS の scroll-margin-top がそれを使う。 */

  var HEADER_SEL = '.shopify-section-group-header-group, sticky-header, .section-header';

  function stickyHeaderHeight() {
    var best = 0;
    var els = document.querySelectorAll(HEADER_SEL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
      /* 上端に貼りつくものだけ(下に固定された相談ボタンなどは数えない) */
      var top = parseFloat(cs.top);
      if (isNaN(top) || top > 8) continue;
      /* いま画面に出ている高さ。スクロールで縮むヘッダーでも実測値になる */
      var h = el.getBoundingClientRect().height;
      if (h > best) best = h;
    }
    return Math.round(best);
  }

  var lastHeader = -1;

  function syncHeaderHeight() {
    var h = stickyHeaderHeight();
    if (h === lastHeader) return;
    lastHeader = h;
    document.documentElement.style.setProperty('--wc2-header-h', h + 'px');
  }

  function anchorOffset() {
    var h = lastHeader;
    if (h < 0) {
      var css = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      h = parseFloat(css) || 0;
    }
    return h + ANCHOR_GAP;
  }

  /* レイアウトが確定したあとに1回だけ走らせる。
     requestAnimationFrame を2回はさむ。タブが裏にあるなど rAF が止まる
     ところでも動くように、時間でも受けて早いほうを使う。 */
  function afterLayout(fn) {
    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      fn();
    };
    requestAnimationFrame(function () {
      requestAnimationFrame(run);
    });
    setTimeout(run, 100);
  }

  /* ------------------------------------------- 目的地へ移動しハイライト */

  var flashTimer = null;
  var flashEl = null;

  /* 移動先が「あとから高さや表示が変わるもの」の中にあると、
     移動先の計算がずれる。先に確定させてから測る。 */
  function settle(el) {
    /* 折りたたみ(details)は先に開く。入れ子も上までたどる */
    var node = el;
    while (node && node.closest) {
      var d = node.closest('details');
      if (!d) break;
      if (!d.open) d.open = true;
      node = d.parentNode;
    }
    /* フェードイン前(transform が効いている)なら先に表示状態にする */
    var fade = el.closest ? el.closest('.wc2-anim, .wc2-band') : null;
    if (fade && !fade.classList.contains('is-in')) fade.classList.add('is-in');
    var inner = el.querySelectorAll ? el.querySelectorAll('.wc2-anim, .wc2-band') : [];
    for (var i = 0; i < inner.length; i++) inner[i].classList.add('is-in');
    /* 遅延読み込みの画像は先に読ませる(読み込みで高さが動かないようにする) */
    var imgs = el.querySelectorAll ? el.querySelectorAll('img[loading="lazy"]') : [];
    for (var j = 0; j < imgs.length; j++) imgs[j].loading = 'eager';
  }

  function goTo(id, push) {
    if (!id) return;
    var el = document.getElementById(id);
    if (!el) return;

    settle(el);

    if (push && window.history && history.replaceState) {
      try {
        history.replaceState(null, '', '#' + id);
      } catch (e) {
        /* 何もしない */
      }
    }

    if (flashEl) flashEl.classList.remove('is-flash');
    if (flashTimer) clearTimeout(flashTimer);
    el.classList.add('is-flash');
    flashEl = el;
    flashTimer = setTimeout(function () {
      el.classList.remove('is-flash');
      flashEl = null;
    }, FLASH_MS);

    /* レイアウトが確定してから測って動かす */
    afterLayout(function () {
      syncHeaderHeight();
      var top = el.getBoundingClientRect().top + window.pageYOffset - anchorOffset();
      var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      top = Math.max(0, Math.min(top, max));
      try {
        window.scrollTo({ top: top, behavior: prefersReduce() ? 'auto' : 'smooth' });
      } catch (e) {
        window.scrollTo(0, top);
      }
    });
  }

  /* -------------------------------------------------- 内訳のシート本体 */

  var sheet = null;
  var backdrop = null;
  var sheetBody = null;
  var sheetClose = null;
  var opener = null;
  var isOpen = false;

  function cacheSheet() {
    sheet = document.getElementById('wc2-price-sheet');
    backdrop = document.getElementById('wc2-price-backdrop');
    sheetBody = document.getElementById('wc2-price-sheet-body');
    sheetClose = document.getElementById('wc2-price-sheet-close');
    return !!(sheet && backdrop && sheetBody);
  }

  function focusables() {
    if (!sheet) return [];
    var list = sheet.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].offsetParent !== null || list[i] === sheet) out.push(list[i]);
    }
    return out;
  }

  function openSheet(exId, btn) {
    if (!cacheSheet()) return;
    var source = document.getElementById('wc2-ex-detail-' + exId);
    if (!source) return;

    opener = btn || null;
    sheetBody.innerHTML = source.innerHTML;
    sheetBody.scrollTop = 0;

    /* シート内の「?」を結ぶ */
    var whys = sheetBody.querySelectorAll('[data-wc2-go]');
    for (var w = 0; w < whys.length; w++) {
      whys[w].addEventListener('click', onWhyClick);
    }

    /* aria-labelledby の相手をシート内の見出しに合わせる */
    var title = sheetBody.querySelector('[data-wc2-sheet-title]');
    if (title) {
      title.id = 'wc2-price-sheet-title';
      sheet.setAttribute('aria-labelledby', 'wc2-price-sheet-title');
    } else {
      sheet.removeAttribute('aria-labelledby');
    }

    backdrop.hidden = false;
    sheet.hidden = false;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        backdrop.classList.add('is-show');
        sheet.classList.add('is-show');
      });
    });

    isOpen = true;
    sheet.focus();
  }

  function onWhyClick() {
    var id = this.getAttribute('data-wc2-go');
    closeSheet(true);
    setTimeout(function () {
      goTo(id, true);
    }, prefersReduce() ? 0 : 280);
  }

  function closeSheet(noFocus) {
    if (!isOpen) return;
    isOpen = false;
    backdrop.classList.remove('is-show');
    sheet.classList.remove('is-show');
    document.body.style.overflow = '';

    var finish = function () {
      if (isOpen) return;
      sheet.hidden = true;
      backdrop.hidden = true;
      sheetBody.innerHTML = '';
    };
    if (prefersReduce()) finish();
    else setTimeout(finish, 260);

    if (!noFocus && opener && opener.focus) opener.focus();
    opener = null;
  }

  /* ------------------------------------------------------ 横スクロール */

  function bindRail(wrap) {
    var scroller = wrap.querySelector('.wc2-shots');
    var band = wrap.closest ? wrap.closest('.wc2-band') : null;
    if (!scroller || !band) return;
    var prev = band.querySelector('[data-wc2-arrow="prev"]');
    var next = band.querySelector('[data-wc2-arrow="next"]');
    if (!prev || !next) return;

    function step() {
      var card = scroller.querySelector('.wc2-shot-item');
      return card ? card.offsetWidth + 12 : 240;
    }
    function sync() {
      prev.disabled = scroller.scrollLeft <= 2;
      next.disabled = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 2;
    }
    prev.addEventListener('click', function () {
      scroller.scrollLeft -= step();
    });
    next.addEventListener('click', function () {
      scroller.scrollLeft += step();
    });
    scroller.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    sync();
  }

  /* -------------------------------------------------------- フェードイン */

  function bindFade(root) {
    var targets = root.querySelectorAll('.wc2-band, .wc2-anim');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) || prefersReduce()) {
      for (var a = 0; a < targets.length; a++) targets[a].classList.add('is-in');
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) {
            entries[k].target.classList.add('is-in');
            io.unobserve(entries[k].target);
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );
    for (var t = 0; t < targets.length; t++) io.observe(targets[t]);
  }

  /* -------------------------------------------------------------- 束ね */

  function bindRoot(root) {
    if (!root || root.dataset.wc2Bound === '1') return;
    root.dataset.wc2Bound = '1';

    var shots = root.querySelectorAll('[data-wc2-ex]');
    for (var i = 0; i < shots.length; i++) {
      shots[i].addEventListener('click', function () {
        openSheet(this.getAttribute('data-wc2-ex'), this);
      });
    }

    var gos = root.querySelectorAll('[data-wc2-go]');
    for (var g = 0; g < gos.length; g++) {
      gos[g].addEventListener('click', function () {
        var id = this.getAttribute('data-wc2-go');
        var part = this.getAttribute('data-wc2-part');
        if (part !== null) markPart(root, part);
        goTo(id, true);
      });
    }

    var rails = root.querySelectorAll('.wc2-shots-wrap');
    for (var r = 0; r < rails.length; r++) bindRail(rails[r]);

    bindFade(root);
  }

  function markPart(root, part) {
    var all = root.querySelectorAll('[data-wc2-part]');
    for (var i = 0; i < all.length; i++) {
      all[i].setAttribute('aria-current', all[i].getAttribute('data-wc2-part') === part ? 'true' : 'false');
    }
  }

  /* ------------------------------------------------ 全体で1回だけの結線 */

  var globalBound = false;

  function bindGlobal() {
    if (globalBound) return;
    if (!cacheSheet()) return;
    globalBound = true;

    backdrop.addEventListener('click', function () {
      closeSheet();
    });
    if (sheetClose) {
      sheetClose.addEventListener('click', function () {
        closeSheet();
      });
    }

    document.addEventListener('keydown', function (ev) {
      if (!isOpen) return;
      if (ev.key === 'Escape' || ev.key === 'Esc') {
        ev.preventDefault();
        closeSheet();
        return;
      }
      if (ev.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (ev.shiftKey && (document.activeElement === first || document.activeElement === sheet)) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('hashchange', fromHash);
    window.addEventListener('scroll', syncHeaderHeight, { passive: true });
    window.addEventListener('resize', syncHeaderHeight);
    fromHashOnLoad();
  }

  /* ハッシュ付きURLで開いたとき。
     画像や書体で高さが動いたあとにもう一度そろえる。 */
  function fromHash() {
    var id = location.hash.replace('#', '');
    if (!id) return;
    if (!document.getElementById(id)) return;
    setTimeout(function () {
      goTo(id, false);
    }, 60);
  }

  function fromHashOnLoad() {
    fromHash();
    /* ページ読み込み直後は遅れて読み込まれるものがあるので、1回だけ合わせ直す */
    window.addEventListener('load', function () {
      setTimeout(fromHash, 120);
    });
  }

  function init() {
    syncHeaderHeight();
    var roots = document.querySelectorAll('.wc2-price');
    for (var i = 0; i < roots.length; i++) bindRoot(roots[i]);
    bindGlobal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* テーマエディタでセクションを編集したとき */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () {
    if (isOpen) closeSheet(true);
  });
})();
